import { StandardEvents } from '@shopify/events'

class KravenProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')]
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')]
    this.track = this.querySelector('[data-gallery-track]')
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')))
    this.controller = new AbortController()
    this.scrollFrame = 0
    this.programmaticIndex = null
    this.programmaticTimer = 0
    this.loopTimer = 0
    this.isRepositioning = false
    this.isGalleryVisible = false
    this.variantMediaMap = this.parseVariantMediaMap()

    this.setupInfiniteTrack()
    this.setupMediaPlayback()

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.show(Number(thumb.dataset.galleryThumb)))
    })

    this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.step(-1))
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1))

    this.track?.addEventListener('scroll', this.handleScroll, { passive: true, signal: this.controller.signal })
    this.setupPointerDrag()

    requestAnimationFrame(() => {
      this.jumpToRealSlide(this.index)
      this.setActive(this.index, false)
      this.syncMediaPlayback()
    })

    this.closest('.shopify-section')?.addEventListener(
      StandardEvents.productSelect,
      this.handleProductSelect,
      { signal: this.controller.signal }
    )
    const productComponent = this.closest('product-component')
    productComponent?.addEventListener('change', this.handleVariantInput, { signal: this.controller.signal })
    productComponent?.addEventListener('click', this.handleVariantInput, { signal: this.controller.signal })
  }

  disconnectedCallback() {
    this.controller?.abort()
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame)
    if (this.programmaticTimer) window.clearTimeout(this.programmaticTimer)
    if (this.loopTimer) window.clearTimeout(this.loopTimer)
    this.galleryObserver?.disconnect()
    this.pauseAllMedia()
  }

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return

    event.promise
      .then(({ detail }) => {
        const responseGallery = detail?.html?.querySelector('kraven-product-gallery')
        const responseActiveSlide = responseGallery?.querySelector('[data-gallery-slide].is-active')
        const responseVariant = this.parseResponseVariant(detail?.html)
        const variantId = detail?.resource?.id ?? responseVariant?.id
        const responseVariantMediaMap = this.parseVariantMediaMap(responseGallery)
        const mediaCandidates = [
          this.variantMediaMap[String(variantId)],
          responseVariantMediaMap[String(variantId)],
          detail?.resource?.featured_media?.id,
          responseVariant?.featured_media?.id,
          responseGallery?.dataset.selectedVariantMediaId,
          detail?.resource?.featured_image?.id,
          responseVariant?.featured_image?.id,
        ]
        let nextIndex = -1
        for (const mediaId of mediaCandidates) {
          nextIndex = this.indexForMedia(mediaId)
          if (nextIndex >= 0) break
        }
        if (nextIndex < 0 && variantId == null && responseActiveSlide) {
          nextIndex = Number(responseActiveSlide.dataset.gallerySlide)
        }
        if (nextIndex >= 0) this.show(nextIndex)
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kraven-product-gallery] Variant media update failed:', error)
      })
  }

  handleVariantInput = (event) => {
    const origin = event.target
    if (!(origin instanceof Element)) return
    const target =
      origin instanceof HTMLSelectElement
        ? origin.selectedOptions[0]
        : origin.closest('variant-picker label')?.querySelector('input[data-option-value-id]') ??
          origin.closest('variant-picker input[data-option-value-id]')
    if (!(target instanceof HTMLElement)) return
    const mediaId =
      target.dataset.variantMediaId ||
      this.variantMediaMap[String(target.dataset.variantId)] ||
      (target.matches(':checked') ? this.dataset.selectedVariantMediaId : null)
    const nextIndex = this.indexForMedia(mediaId)
    if (nextIndex >= 0) this.show(nextIndex)
  }

  parseVariantMediaMap(root = this) {
    const source = root?.querySelector('[data-variant-media-map]')?.textContent
    if (!source) return {}
    try {
      return JSON.parse(source)
    } catch {
      return {}
    }
  }

  parseResponseVariant(html) {
    const source = html?.querySelector('variant-picker script[type="application/json"]')?.textContent
    if (!source) return null
    try {
      return JSON.parse(source)
    } catch {
      return null
    }
  }

  indexForMedia(mediaId) {
    if (mediaId == null) return -1
    return this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId))
  }

  step(direction) {
    if (this.slides.length < 2) return
    const nextIndex = this.index + direction
    if (nextIndex >= this.slides.length) {
      this.animateToClone('end', 0)
      return
    }
    if (nextIndex < 0) {
      this.animateToClone('start', this.slides.length - 1)
      return
    }
    this.show(nextIndex)
  }

  show(nextIndex) {
    if (!Number.isInteger(nextIndex) || nextIndex < 0 || nextIndex >= this.slides.length) return
    const targetLeft = this.slides[nextIndex]?.offsetLeft ?? 0
    this.programmaticIndex = nextIndex
    if (this.programmaticTimer) window.clearTimeout(this.programmaticTimer)
    this.setActive(nextIndex)
    this.track?.scrollTo({ left: targetLeft, behavior: 'smooth' })
    this.programmaticTimer = window.setTimeout(() => {
      this.programmaticIndex = null
    }, 900)
  }

  setActive(nextIndex, scrollThumb = true) {
    this.slides.forEach((slide, index) => {
      const active = index === nextIndex
      slide.classList.toggle('is-active', active)
      slide.setAttribute('aria-hidden', String(!active))
    })
    this.thumbs.forEach((thumb, index) => {
      const active = index === nextIndex
      thumb.classList.toggle('is-active', active)
      thumb.setAttribute('aria-selected', String(active))
      if (active && scrollThumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    })

    this.index = nextIndex
    requestAnimationFrame(() => this.syncMediaPlayback())
  }

  handleScroll = () => {
    if (this.scrollFrame || !this.track) return
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0
      this.syncMediaPlayback()
      if (this.isRepositioning || this.programmaticIndex !== null) return
      const nearest = this.nearestTrackSlide()
      const nextIndex = Number(nearest?.dataset.logicalIndex)
      if (!Number.isInteger(nextIndex)) return
      if (nextIndex !== this.index) this.setActive(nextIndex)
      if (this.loopTimer) window.clearTimeout(this.loopTimer)
      this.loopTimer = window.setTimeout(() => this.normalizeLoopPosition(), 140)
    })
  }

  setupPointerDrag() {
    if (!this.track) return
    let startX = 0
    let startScroll = 0
    let dragging = false

    this.track.addEventListener('pointerdown', (event) => {
      this.programmaticIndex = null
      if (this.programmaticTimer) window.clearTimeout(this.programmaticTimer)
      if (this.loopTimer) window.clearTimeout(this.loopTimer)
      if (event.pointerType === 'touch') return
      dragging = true
      startX = event.clientX
      startScroll = this.track.scrollLeft
      this.track.classList.add('is-dragging')
      this.track.setPointerCapture(event.pointerId)
    }, { signal: this.controller.signal })

    this.track.addEventListener('pointermove', (event) => {
      if (!dragging) return
      this.track.scrollLeft = startScroll - (event.clientX - startX)
    }, { signal: this.controller.signal })

    const finishDrag = (event) => {
      if (!dragging) return
      dragging = false
      this.track.classList.remove('is-dragging')
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId)
      this.snapToNearestTrackSlide()
    }

    this.track.addEventListener('pointerup', finishDrag, { signal: this.controller.signal })
    this.track.addEventListener('pointercancel', finishDrag, { signal: this.controller.signal })
  }

  setupInfiniteTrack() {
    this.slides.forEach((slide, index) => {
      slide.dataset.logicalIndex = String(index)
    })
    if (!this.track || this.slides.length < 2) {
      this.trackSlides = this.slides
      return
    }

    const cloneCount = Math.min(2, this.slides.length)
    const startFragment = document.createDocumentFragment()
    const endFragment = document.createDocumentFragment()

    this.slides.slice(-cloneCount).forEach((slide) => {
      startFragment.append(this.createClone(slide, 'start'))
    })
    this.slides.slice(0, cloneCount).forEach((slide) => {
      endFragment.append(this.createClone(slide, 'end'))
    })

    this.track.prepend(startFragment)
    this.track.append(endFragment)
    this.trackSlides = [...this.track.querySelectorAll('[data-gallery-slide]')]
  }

  createClone(slide, side) {
    const clone = slide.cloneNode(true)
    clone.classList.remove('is-active')
    clone.dataset.galleryClone = side
    clone.setAttribute('aria-hidden', 'true')
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'))
    clone.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy'
      image.removeAttribute('fetchpriority')
    })
    clone.querySelectorAll('video').forEach((video) => {
      video.autoplay = false
      video.removeAttribute('autoplay')
      video.preload = 'none'
      this.muteVideo(video)
    })
    return clone
  }

  setupMediaPlayback() {
    this.querySelectorAll('video').forEach((video) => this.muteVideo(video))
    this.galleryObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        this.isGalleryVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15)
        this.syncMediaPlayback()
      },
      { threshold: [0, 0.15, 0.5] }
    )
    this.galleryObserver.observe(this)
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal: this.controller.signal })
  }

  handleVisibilityChange = () => {
    this.syncMediaPlayback()
  }

  muteVideo(video) {
    video.muted = true
    video.defaultMuted = true
    video.volume = 0
    video.loop = true
    video.playsInline = true
    video.setAttribute('muted', '')
    video.setAttribute('loop', '')
    video.setAttribute('playsinline', '')
  }

  syncMediaPlayback() {
    const activeSlide = this.nearestTrackSlide() ?? this.slides[this.index]
    const canPlay = this.isGalleryVisible && !document.hidden

    this.trackSlides?.forEach((slide) => {
      const shouldPlay = canPlay && slide === activeSlide
      const video = slide.querySelector('video')

      if (video instanceof HTMLVideoElement) {
        this.muteVideo(video)
        if (shouldPlay) {
          const playPromise = video.play()
          if (playPromise instanceof Promise) playPromise.catch(() => {})
        } else {
          video.pause()
        }
      }

      const iframe = shouldPlay ? this.loadExternalVideo(slide) : slide.querySelector('iframe[data-video-type]')
      if (iframe instanceof HTMLIFrameElement) this.setExternalVideoPlaying(iframe, shouldPlay)
    })
  }

  loadExternalVideo(slide) {
    const container = slide.querySelector('[data-gallery-external-video]')
    if (!(container instanceof HTMLElement)) return null
    const existing = container.querySelector('iframe[data-video-type]')
    if (existing instanceof HTMLIFrameElement) return existing
    const content = container.querySelector('template[data-gallery-external-template]')?.content.firstElementChild
    if (!(content instanceof HTMLIFrameElement)) return null
    const iframe = content.cloneNode(true)
    container.append(iframe)
    return iframe
  }

  setExternalVideoPlaying(iframe, shouldPlay) {
    const type = iframe.dataset.videoType
    try {
      if (type === 'youtube') {
        iframe.contentWindow?.postMessage('{"event":"command","func":"mute","args":""}', '*')
        iframe.contentWindow?.postMessage(
          `{"event":"command","func":"${shouldPlay ? 'playVideo' : 'pauseVideo'}","args":""}`,
          '*'
        )
      } else {
        iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*')
        iframe.contentWindow?.postMessage(`{"method":"${shouldPlay ? 'play' : 'pause'}"}`, '*')
      }
    } catch {}
  }

  pauseAllMedia() {
    this.querySelectorAll('video').forEach((video) => video.pause())
    this.querySelectorAll('iframe[data-video-type]').forEach((iframe) => this.setExternalVideoPlaying(iframe, false))
  }

  animateToClone(side, logicalIndex) {
    const clone = this.trackSlides?.find(
      (slide) => slide.dataset.galleryClone === side && Number(slide.dataset.logicalIndex) === logicalIndex
    )
    if (!clone || !this.track) {
      this.show(logicalIndex)
      return
    }

    this.programmaticIndex = logicalIndex
    if (this.programmaticTimer) window.clearTimeout(this.programmaticTimer)
    const targetLeft = clone.offsetLeft
    this.setActive(logicalIndex)
    this.track.scrollTo({ left: targetLeft, behavior: 'smooth' })
    this.programmaticTimer = window.setTimeout(() => {
      this.jumpToRealSlide(logicalIndex)
      this.programmaticIndex = null
    }, 900)
  }

  nearestTrackSlide() {
    if (!this.track || !this.trackSlides?.length) return null
    const trackLeft = this.track.scrollLeft
    return this.trackSlides.reduce((closest, slide) => {
      const currentDistance = Math.abs(slide.offsetLeft - trackLeft)
      const closestDistance = Math.abs(closest.offsetLeft - trackLeft)
      return currentDistance < closestDistance ? slide : closest
    })
  }

  snapToNearestTrackSlide() {
    const nearest = this.nearestTrackSlide()
    if (!nearest || !this.track) return
    const targetLeft = nearest.offsetLeft
    const logicalIndex = Number(nearest.dataset.logicalIndex)
    if (Number.isInteger(logicalIndex)) this.setActive(logicalIndex)
    this.track.scrollTo({ left: targetLeft, behavior: 'smooth' })
    if (this.loopTimer) window.clearTimeout(this.loopTimer)
    this.loopTimer = window.setTimeout(() => this.normalizeLoopPosition(), 900)
  }

  normalizeLoopPosition() {
    const nearest = this.nearestTrackSlide()
    if (!nearest?.dataset.galleryClone) return
    const logicalIndex = Number(nearest.dataset.logicalIndex)
    if (Number.isInteger(logicalIndex)) this.jumpToRealSlide(logicalIndex)
  }

  jumpToRealSlide(index) {
    if (!this.track || !this.slides[index]) return
    const targetLeft = this.slides[index].offsetLeft
    this.isRepositioning = true
    const previousBehavior = this.track.style.scrollBehavior
    this.track.style.scrollBehavior = 'auto'
    this.track.scrollLeft = targetLeft
    requestAnimationFrame(() => {
      this.track.style.scrollBehavior = previousBehavior
      this.isRepositioning = false
      this.syncMediaPlayback()
    })
  }
}

if (!customElements.get('kraven-product-gallery')) {
  customElements.define('kraven-product-gallery', KravenProductGallery)
}
